import React from "react";
import { Button, Modal } from "../../../components/ui";
// The design's own glyph — a white crown on the purple disc it is drawn with,
// so nothing here puts a background behind it. The same file is used at 30px
// beside the confirmation line on the page once this dialog is answered.
import crownIcon from "../../../assets/svg/create page/crown icon.svg";

export interface EarlyAccessModalProps {
  open: boolean;
  /** They want in. The page records it and stops offering. */
  onJoin: () => void;
  /** Closes without joining — "I'll join later", Escape and the backdrop. */
  onClose: () => void;
}

/**
 * What "Get early access" asks before it counts anybody in: three lines saying
 * what being on the list actually buys, and two answers.
 *
 * No heading bar — the crown, the sentence under it and the list are the whole
 * message, and a "Priority list" title above a panel that says it better would
 * be the same thing twice. The dialog is still named to a screen reader, and
 * the visible copy of that name is hidden from it so it is not read out again;
 * see `Modal`'s `hideTitle` and `RefundDoneModal`, which does the same.
 *
 * The backdrop dismisses, unlike the first-run brand dialog: a miss here costs
 * nothing, because the button that opened it is still on the page afterwards.
 */
const EarlyAccessModal: React.FC<EarlyAccessModalProps> = ({
  open,
  onJoin,
  onClose,
}) => (
  <Modal
    open={open}
    title="You'll be on priority list!"
    hideTitle
    onClose={onClose}
    className="max-w-[500px]"
    footer={
      <>
        <Button variant="secondary" size="md" onClick={onClose}>
          I&apos;ll join later
        </Button>
        <Button variant="primary" size="md" onClick={onJoin}>
          I am interested
        </Button>
      </>
    }
  >
    <img src={crownIcon} alt="" aria-hidden="true" className="h-14 w-14" />

    <p
      aria-hidden="true"
      className="mt-4 text-[20px] 3xl:text-[22px] font-semibold text-[#007E8C]"
    >
      You&apos;ll be on priority list!
    </p>
    <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
      To claim your personalised url
    </p>

    {/* A real list, so a screen reader announces how many promises are being
        made rather than reading three sentences that happen to sit together. */}
    <ul className="mt-5 list-disc space-y-2 pl-5 text-[13px] 3xl:text-[14px] leading-[1.6] text-ink-muted marker:text-ink-muted">
      <li>Get early access</li>
      <li>
        Claim your personalised URL - naurist.com/
        {/* The part that would be theirs, in the brand teal — it is the one
            piece of the line that is a stand-in for their own name. */}
        <span className="text-brand">your-name</span>
      </li>
      <li>Launch your portfolio page</li>
    </ul>

    {/* Runs to the panel's edges, cancelling `Modal`'s body padding, so the rule
        separates the message from the actions rather than floating inside it. */}
    <div className="-mx-4 mt-6 border-t border-line sm:-mx-6" />
  </Modal>
);

export default EarlyAccessModal;
